import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { swalConfirm } from "../../../utils/swalAlert"; // ← مسیر swalAlert.js را درست کن
import "../../../styles/proMajazi/admin/ProMajaziAdmin.css";
import http from "../../../api/http"; // ← مسیر http را درست کن
import {
  getAdminStoresData,
  getAdminPaymentsData,
  getAdminSettingData,
  status_token,
  getShopCustomers,
} from "../../../api/proMajazi"; // ← مسیر فایلی که این توابع در آن هستند
import moment from "jalali-moment";
import { convertToPersianDigits } from "../../../utils/stringUtil";

/* ------------------------------------------------------------------
   API — اتصال به سرویس‌ها
------------------------------------------------------------------- */

// اگر http شما axios است پاسخ در res.data است، در غیر این صورت خود res برمی‌گردد
const unwrap = (res) => res?.data ?? res;

// پیام خطا از بک‌اند یا پیام پیش‌فرض
const errMsg = (e) =>
  e?.response?.data?.message || e?.message || "خطا در ارتباط با سرور";

const api = {
  getSettings: async () => unwrap(await getAdminSettingData()), // { tokenPriceUsd }
  getShops: async () => unwrap(await getAdminStoresData()), // [{ id, name, ownerName, tokens, usedTokens }]
  getPayments: async () => unwrap(await getAdminPaymentsData()), // [{ id, shopId, shopName, tokens, amountRial, method, receiptUrl, status, createdAt }]

  // { store_id, store_name, total_customers, customers: [{ customer_name, customer_mobile, used_tokens }] }
  // sort: highest_tokens | lowest_tokens | newest | oldest
  getShopCustomers: async (shopId, sort) =>
    unwrap(await getShopCustomers(shopId, sort)),

  saveSettings: async (tokenPriceUsd) =>
    unwrap(await http.put(`/pro-majazi/admin/settings`, { tokenPriceUsd })),

  // تایید / رد واریزی از طریق status_token
  // بدنه‌ی ارسالی: { action: "approve" | "reject" }
  approvePayment: async (id) => unwrap(await status_token(id, "approve")),
  rejectPayment: async (id) => unwrap(await status_token(id, "reject")),
};

const fa = (n) => Number(n || 0).toLocaleString("fa-IR");

const STATUS = {
  pending: { label: "در انتظار تایید", cls: "pma-badge--pending" },
  paid: { label: "تایید شده", cls: "pma-badge--approved" },
  rejected: { label: "رد شده", cls: "pma-badge--rejected" },
};

// کلیدها دقیقاً همان مقادیری‌اند که بک‌اند می‌پذیرد (highest_tokens / lowest_tokens / newest / oldest)
const CUSTOMER_SORTS = [
  ["highest_tokens", "بیشترین توکن"],
  ["lowest_tokens", "کمترین توکن"],
  ["newest", "جدیدترین"],
  ["oldest", "قدیمی‌ترین"],
];

const METHOD = { gateway: "درگاه پرداخت", card: "کارت به کارت" };

/* ------------------------------------------------------------------
   صفحه‌بندی (سمت کلاینت)
------------------------------------------------------------------- */
const PAGE_SIZE = 7;

// resetKey عوض شود (فیلتر، جستجو، فروشگاه دیگر) صفحه به ۱ برمی‌گردد
function usePagination(items, resetKey, pageSize = PAGE_SIZE) {
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [resetKey]);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * pageSize;

  return {
    page: current,
    setPage,
    total,
    totalPages,
    pageSize,
    pageItems: items.slice(start, start + pageSize),
  };
}

const pageNumbers = (page, totalPages) => {
  const nums = [...new Set([1, totalPages, page - 1, page, page + 1])]
    .filter((n) => n >= 1 && n <= totalPages)
    .sort((a, b) => a - b);
  const out = [];
  nums.forEach((n, i) => {
    if (i > 0 && n - nums[i - 1] > 1) out.push("gap");
    out.push(n);
  });
  return out;
};

function Pagination({ pager }) {
  const { page, setPage, total, totalPages, pageSize } = pager;
  if (total <= pageSize) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <nav className="pma-pager" aria-label="صفحه‌بندی">
      <span className="pma-pager-info">
        نمایش {fa(from)} تا {fa(to)} از {fa(total)}
      </span>
      <div className="pma-pager-btns">
        <button
          className="pma-btn pma-btn--ghost"
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
        >
          قبلی
        </button>
        {pageNumbers(page, totalPages).map((n, i) =>
          n === "gap" ? (
            <span key={`gap-${i}`} className="pma-pager-gap">
              …
            </span>
          ) : (
            <button
              key={n}
              className={`pma-btn pma-btn--ghost pma-pager-num ${
                n === page ? "is-active" : ""
              }`}
              aria-current={n === page ? "page" : undefined}
              onClick={() => setPage(n)}
            >
              {fa(n)}
            </button>
          ),
        )}
        <button
          className="pma-btn pma-btn--ghost"
          disabled={page === totalPages}
          onClick={() => setPage(page + 1)}
        >
          بعدی
        </button>
      </div>
    </nav>
  );
}

/* ------------------------------------------------------------------
   سلکت سفارشی (لیست بازشونده‌ی قابل استایل)
------------------------------------------------------------------- */
function SortSelect({ id, value, options, onChange, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);

  const selectedIndex = Math.max(
    0,
    options.findIndex(([key]) => key === value),
  );
  const selectedLabel = options[selectedIndex]?.[1];

  const openMenu = () => {
    setActive(selectedIndex);
    setOpen(true);
  };

  const choose = (key) => {
    onChange(key);
    setOpen(false);
  };

  // بستن با کلیک بیرون
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const onKeyDown = (e) => {
    const last = options.length - 1;
    switch (e.key) {
      case "Escape":
        if (open) {
          e.stopPropagation(); // مودال بسته نشود
          setOpen(false);
        }
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!open) openMenu();
        else setActive((i) => Math.min(last, i + 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!open) openMenu();
        else setActive((i) => Math.max(0, i - 1));
        break;
      case "Home":
        if (open) {
          e.preventDefault();
          setActive(0);
        }
        break;
      case "End":
        if (open) {
          e.preventDefault();
          setActive(last);
        }
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (open) choose(options[active][0]);
        else openMenu();
        break;
      case "Tab":
        setOpen(false);
        break;
      default:
    }
  };

  return (
    <div className="pma-dd" ref={rootRef} onKeyDown={onKeyDown}>
      <button
        type="button"
        id={id}
        className={`pma-dd-trigger ${open ? "is-open" : ""}`}
        role="combobox"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-activedescendant={open ? `${id}-opt-${active}` : undefined}
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        <span>{selectedLabel}</span>
        <svg
          className="pma-dd-arrow"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <ul className="pma-dd-menu" id={`${id}-list`} role="listbox">
          {options.map(([key, label], i) => (
            <li
              key={key}
              id={`${id}-opt-${i}`}
              role="option"
              aria-selected={key === value}
              className={`pma-dd-option ${key === value ? "is-selected" : ""} ${
                i === active ? "is-active" : ""
              }`}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(key)}
            >
              <span>{label}</span>
              {key === value && (
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12l5 5L20 7" />
                </svg>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ProMajaziAdmin() {
  const [tab, setTab] = useState("payments");
  const [toast, setToast] = useState(null);

  const [price, setPrice] = useState("");
  const [savedPrice, setSavedPrice] = useState(null);
  const [savingPrice, setSavingPrice] = useState(false);

  const [shops, setShops] = useState([]);
  const [shopQuery, setShopQuery] = useState("");

  const [customersShop, setCustomersShop] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customersTotal, setCustomersTotal] = useState(0);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState(null);
  const [custSort, setCustSort] = useState("newest");

  const [payments, setPayments] = useState([]);
  const [payFilter, setPayFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);

  const notify = (text, type = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadAll = useCallback(async () => {
    try {
      const [s, sh, pay] = await Promise.all([
        api.getSettings(),
        api.getShops(),
        api.getPayments(),
      ]);
      setSavedPrice(s.tokenPriceUsd);
      setPrice(String(s.tokenPriceUsd));
      setShops(sh);
      setPayments(pay);
    } catch (e) {
      notify(errMsg(e), "err");
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  /* ---------- مشتریان پرو شده‌ی هر فروشگاه ---------- */
  useEffect(() => {
    if (!customersShop) return;
    let cancelled = false;
    setCustomersError(null);
    setCustomersLoading(true);
    api
      .getShopCustomers(customersShop.id, custSort)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.customers) ? data.customers : [];
        setCustomers(list);
        setCustomersTotal(data?.total_customers ?? list.length);
      })
      .catch((e) => {
        if (!cancelled) setCustomersError(errMsg(e));
      })
      .finally(() => {
        if (!cancelled) setCustomersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customersShop, custSort]);

  // باز کردن لیست مشتریان یک فروشگاه (ریست مرتب‌سازی و لیست قبلی)
  const openCustomers = (shop) => {
    setCustSort("newest");
    setCustomers([]);
    setCustomersTotal(0);
    setCustomersShop(shop);
  };

  useEffect(() => {
    if (!customersShop) return;
    const onKey = (e) => e.key === "Escape" && setCustomersShop(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [customersShop]);

  /* ---------- قیمت توکن ---------- */
  const priceNum = parseFloat(price);
  const priceValid = Number.isFinite(priceNum) && priceNum > 0;
  const priceChanged = priceValid && priceNum !== savedPrice;

  const handleSavePrice = async () => {
    if (!priceValid) return;
    setSavingPrice(true);
    try {
      await api.saveSettings(priceNum);
      setSavedPrice(priceNum);
      notify("قیمت توکن ذخیره شد");
    } catch (e) {
      notify(errMsg(e), "err");
    } finally {
      setSavingPrice(false);
    }
  };

  /* ---------- فروشگاه‌ها ---------- */
  const filteredShops = useMemo(() => {
    const q = shopQuery.trim();
    const list = q
      ? shops.filter(
          (s) =>
            s.name?.includes(q) ||
            s.ownerName?.includes(q) ||
            String(s.id).includes(q),
        )
      : shops;
    return [...list].sort((a, b) => (b.tokens || 0) - (a.tokens || 0));
  }, [shops, shopQuery]);

  const totalTokens = useMemo(
    () => shops.reduce((sum, s) => sum + (s.tokens || 0), 0),
    [shops],
  );

  const shopPager = usePagination(filteredShops, shopQuery);
  const custPager = usePagination(
    customers,
    `${customersShop?.id}-${custSort}`,
  );

  /* ---------- واریزی‌ها ---------- */
  const pendingCount = payments.filter((p) => p.status === "pending").length;
  const filteredPayments = payments.filter((p) => p.status === payFilter);
  const payPager = usePagination(filteredPayments, payFilter);

  const handlePayment = async (payment, action) => {
    const isApprove = action === "approve";
    const msg = isApprove
      ? `واریزی «${payment.shopName}» به مبلغ ${fa(payment.amountRial)} ریال تایید و ${fa(
          payment.tokens,
        )} توکن به فروشگاه اضافه شود؟`
      : `واریزی «${payment.shopName}» رد شود؟`;
    const ok = await swalConfirm({
      title: isApprove ? "تایید واریزی" : "رد واریزی",
      text: msg,
      confirmText: isApprove ? "بله، تایید شود" : "بله، رد شود",
      cancelText: "انصراف",
      danger: !isApprove,
    });
    if (!ok) return;

    setBusyId(payment.id);
    try {
      await (isApprove
        ? api.approvePayment(payment.id)
        : api.rejectPayment(payment.id));
      setPayments((prev) =>
        prev.map((p) =>
          p.id === payment.id
            ? { ...p, status: isApprove ? "paid" : "rejected" }
            : p,
        ),
      );
      if (isApprove) {
        setShops((prev) =>
          prev.map((s) =>
            s.id === payment.shopId
              ? { ...s, tokens: (s.tokens || 0) + payment.tokens }
              : s,
          ),
        );
      }
      notify(isApprove ? "واریزی تایید شد" : "واریزی رد شد");
    } catch (e) {
      notify(errMsg(e), "err");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="pma" dir="rtl">
      <header className="pma-head">
        <h1>مدیریت پرو مجازی</h1>
        <div className="pma-tabs" role="tablist">
          {[
            [
              "payments",
              `واریزی‌ها${pendingCount ? ` (${fa(pendingCount)})` : ""}`,
            ],
            ["shops", "فروشگاه‌ها"],
            ["price", "قیمت توکن"],
          ].map(([key, label]) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              className={`pma-tab ${tab === key ? "is-active" : ""}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      {/* ============ واریزی‌ها ============ */}
      {tab === "payments" && (
        <section className="pma-panel">
          <div className="pma-filters">
            {Object.entries(STATUS).map(([key, { label }]) => (
              <button
                key={key}
                className={`pma-chip ${payFilter === key ? "is-active" : ""}`}
                onClick={() => setPayFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {filteredPayments.length === 0 ? (
            <p className="pma-empty">واریزی‌ای در این وضعیت وجود ندارد.</p>
          ) : (
            <div className="pma-table-wrap">
              <table className="pma-table">
                <thead>
                  <tr>
                    <th>فروشگاه</th>
                    <th>تعداد توکن</th>
                    <th>مبلغ (تومان)</th>
                    <th>روش پرداخت</th>
                    <th>شناسه واریز</th>
                    <th>مبلغ پرداختی برای خرید توکن</th>

                    <th>تاریخ</th>
                    <th>وضعیت</th>
                    <th>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {payPager.pageItems.map((p) => (
                    <tr key={p.id}>
                      <td data-label="فروشگاه">{p.shopName}</td>
                      <td data-label="تعداد توکن">{fa(p.tokens)}</td>
                      <td data-label="مبلغ (ریال)">{fa(p.amountRial)}</td>
                      <td data-label="روش پرداخت">
                        {METHOD[p.method] || p.method}
                      </td>
                      <td data-label="شناسه واریز">
                        {convertToPersianDigits(p.receiptUrl)}
                      </td>
                      <td data-label="مبلغ پرداختی برای خرید توکن">
                        {`${convertToPersianDigits(p.tokens_to_buy)} $`}
                      </td>
                      <td data-label="تاریخ">
                        {convertToPersianDigits(
                          moment(p.createdAt).format("HH:mm - jYYYY/jMM/jDD"),
                        )}
                      </td>

                      <td data-label="وضعیت">
                        <span className={`pma-badge ${STATUS[p.status]?.cls}`}>
                          {STATUS[p.status]?.label}
                        </span>
                      </td>
                      <td data-label="عملیات">
                        {p.status === "pending" ? (
                          <div className="pma-actions">
                            <button
                              className="pma-btn pma-btn--ok"
                              disabled={busyId === p.id}
                              onClick={() => handlePayment(p, "approve")}
                            >
                              تایید
                            </button>
                            <button
                              className="pma-btn pma-btn--danger"
                              disabled={busyId === p.id}
                              onClick={() => handlePayment(p, "reject")}
                            >
                              رد
                            </button>
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination pager={payPager} />
        </section>
      )}

      {/* ============ فروشگاه‌ها ============ */}
      {tab === "shops" && (
        <section className="pma-panel">
          <div className="pma-summary">
            <div>
              <span>تعداد فروشگاه‌ها</span>
              <strong>{fa(shops.length)}</strong>
            </div>
            <div>
              <span>مجموع توکن‌های موجود</span>
              <strong>{fa(totalTokens)}</strong>
            </div>
          </div>

          <input
            className="pma-input"
            type="search"
            placeholder="جستجو بر اساس نام فروشگاه  "
            value={shopQuery}
            onChange={(e) => setShopQuery(e.target.value)}
          />

          {filteredShops.length === 0 ? (
            <p className="pma-empty">فروشگاهی پیدا نشد.</p>
          ) : (
            <div className="pma-table-wrap">
              <table className="pma-table">
                <thead>
                  <tr>
                    <th>فروشگاه</th>
                    <th>توکن خریداری شده</th>
                    <th>توکن باقی‌مانده</th>
                    <th>توکن مصرف‌شده</th>
                    <th>مشتریان پرو شده</th>
                  </tr>
                </thead>
                <tbody>
                  {shopPager.pageItems.map((s) => (
                    <tr key={s.id}>
                      <td data-label="فروشگاه">{s.name}</td>
                      <td data-label="توکن خریداری">
                        {fa(s.total_token_buy) ?? 0}
                      </td>
                      <td data-label="توکن باقی‌مانده">
                        <strong>{fa(s.tokens)}</strong>
                      </td>
                      <td data-label="توکن مصرف‌شده">{fa(s.usedTokens)}</td>
                      <td data-label="مشتریان پرو شده">
                        <button
                          className="pma-btn pma-btn--ghost"
                          onClick={() => openCustomers(s)}
                        >
                          مشاهده لیست
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination pager={shopPager} />
        </section>
      )}

      {/* ============ قیمت توکن ============ */}
      {tab === "price" && (
        <section className="pma-panel pma-price">
          <label htmlFor="tokenPrice">قیمت هر توکن (دلار)</label>
          <div className="pma-price-row">
            <span className="pma-prefix">$</span>
            <input
              id="tokenPrice"
              className="pma-input"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              dir="ltr"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
            <button
              className="pma-btn pma-btn--primary"
              onClick={handleSavePrice}
              disabled={!priceChanged || savingPrice}
            >
              {savingPrice ? "در حال ذخیره…" : "ذخیره قیمت"}
            </button>
          </div>
          {!priceValid && price !== "" && (
            <p className="pma-error">قیمت باید عددی بزرگ‌تر از صفر باشد.</p>
          )}
          {priceValid && (
            <p className="pma-hint">
              هر 1 توکن = $
              {(priceNum * 100).toLocaleString("en-US", {
                maximumFractionDigits: 4,
              })}
              . مبلغ ریالی هنگام خرید، با نرخ لحظه‌ای دلار محاسبه می‌شود.
            </p>
          )}
          {savedPrice !== null && (
            <p className="pma-hint">قیمت فعلی ثبت‌شده: ${savedPrice}</p>
          )}
        </section>
      )}

      {customersShop && (
        <div
          className="pma-modal-backdrop"
          onClick={() => setCustomersShop(null)}
        >
          <div
            className="pma-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`مشتریان پرو شده «${customersShop.name}»`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pma-modal-head">
              <h2>مشتریان پرو شده‌ی «{customersShop.name}»</h2>
              <button
                className="pma-btn pma-btn--ghost"
                onClick={() => setCustomersShop(null)}
              >
                بستن
              </button>
            </div>

            {customersLoading && customers.length === 0 ? (
              <p className="pma-modal-state">در حال بارگذاری…</p>
            ) : customersError ? (
              <p className="pma-modal-state pma-error">{customersError}</p>
            ) : customers.length === 0 ? (
              <p className="pma-modal-state">
                این فروشگاه هنوز مشتری‌ای را پرو نکرده است.
              </p>
            ) : (
              <>
                <p className="pma-hint pma-modal-count">
                  تعداد: {fa(customersTotal)} مشتری
                </p>
                <div className="pma-sort">
                  <label htmlFor="custSort">مرتب‌سازی:</label>
                  <SortSelect
                    id="custSort"
                    ariaLabel="مرتب‌سازی مشتریان"
                    value={custSort}
                    options={CUSTOMER_SORTS}
                    onChange={setCustSort}
                  />
                </div>
                <div
                  className={`pma-table-wrap ${customersLoading ? "is-loading" : ""}`}
                >
                  <table className="pma-table">
                    <thead>
                      <tr>
                        <th>نام مشتری</th>
                        <th>شماره موبایل</th>
                        <th>توکن مصرف‌شده</th>
                      </tr>
                    </thead>
                    <tbody>
                      {custPager.pageItems.map((c, i) => (
                        <tr key={`${c.customer_mobile}-${i}`}>
                          <td data-label="نام مشتری">
                            {c.customer_name?.trim() || "—"}
                          </td>
                          <td data-label="شماره موبایل">
                            {convertToPersianDigits(c.customer_mobile || "—")}
                          </td>
                          <td data-label="توکن مصرف‌شده">
                            {fa(c.total_used_tokens ?? c.used_tokens)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination pager={custPager} />
              </>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div className={`pma-toast pma-toast--${toast.type}`}>{toast.text}</div>
      )}
    </div>
  );
}
